import { Badge } from "@freightsimple/ui-core/Display/Badge";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { StackedTextCell } from "@freightsimple/ui-core/Cells/StackedTextCell";
import { StatusDotCell } from "@freightsimple/ui-core/Cells/StatusDotCell";
import { Table, type Column } from "@freightsimple/ui-core/Table/Table";
import { TextCell } from "@freightsimple/ui-core/Cells/TextCell";
import { OpportunityAgreementStatus } from "@freightsimple/generated-carriers-openapi-client/models/OpportunityAgreementStatus";
import { type OpportunitySummary } from "@freightsimple/generated-carriers-openapi-client/models/OpportunitySummary";
import { formatMoney, formatPercent } from "@/App/Views/carrierFormatting";
import { opportunityStatusDotColor } from "@/App/Views/opportunityFormatting";
import { OpportunityDialog } from "@/App/Opportunities/OpportunityDialog";
import {
  customerSpecificPricingAgreementHref,
  opportunityHref,
} from "@/App/carrierRoutes";

/**
 * Customers looking for pricing, with this carrier's own position on each.
 *
 * Every carrier sees the same customers — we want competitive tension on this
 * freight, and curating per carrier is manual work that would never keep up.
 * What differs per carrier is the annotation: their agreement, their coverage,
 * and how they already do on the freight.
 */
export function OpportunitiesTable({
  opportunities,
  onChanged,
}: {
  opportunities: OpportunitySummary[];
  onChanged: () => void;
}) {
  const columns: Column<OpportunitySummary>[] = [
    {
      key: "company",
      header: "Customer",
      render: (opportunity) => (
        <StackedTextCell
          primary={opportunity.companyName}
          secondary={opportunity.narrative}
        />
      ),
    },
    {
      key: "volume",
      header: "Shipments",
      align: "right",
      render: (opportunity) => (
        <TextCell
          value={`${opportunity.shipmentsPerYear.toLocaleString("en-CA")}/yr`}
        />
      ),
    },
    {
      key: "spend",
      header: "Freight Spend",
      align: "right",
      render: (opportunity) => (
        <TextCell
          value={
            opportunity.estimatedAnnualFreightSpendCad === undefined
              ? undefined
              : `${formatMoney(opportunity.estimatedAnnualFreightSpendCad)}/yr`
          }
        />
      ),
    },
    {
      key: "coverage",
      header: "Your Coverage",
      align: "right",
      render: (opportunity) => (
        <TextCell
          value={
            opportunity.coveragePercentage === undefined
              ? undefined
              : formatPercent(opportunity.coveragePercentage)
          }
        />
      ),
    },
    {
      key: "standing",
      header: "You Today",
      render: (opportunity) =>
        opportunity.alreadyQuoting ? (
          <TextCell
            value={
              opportunity.winRate === undefined
                ? `Quoting ${opportunity.quotedShipments?.toLocaleString("en-CA") ?? ""}`
                : `Win ${formatPercent(opportunity.winRate)}`
            }
          />
        ) : (
          <TextCell value="Not quoting" color="muted" />
        ),
    },
    {
      key: "status",
      header: "Status",
      render: (opportunity) => (
        <StatusDotCell
          color={opportunityStatusDotColor(opportunity.agreementStatus)}
          label={opportunity.agreementStatusLabel}
        />
      ),
    },
    {
      key: "expires",
      header: "Expires",
      // Carriers aren't strict about renewal dates, so an expiring agreement
      // gets a badge — not a workflow and not a nag.
      render: (opportunity) =>
        opportunity.expiringSoon === true && opportunity.expiresAt ? (
          <Badge label={opportunity.expiresAt} variant="warning" />
        ) : (
          <TextCell value={opportunity.expiresAt} color="muted" />
        ),
    },
  ];

  return (
    // No Panel: the page header already names this list and the count is in the
    // page's header stats, so a titled card here was a second header repeating both.
    <Table
      columns={columns}
      data={opportunities}
      rowKey={(opportunity) => opportunity.companyId}
      emptyMessage="There are no pricing opportunities open right now."
      // Three destinations, by where the carrier stands. Unclaimed freight
      // opens the quick dialog; once they've registered interest the full
      // package page is the useful place; a real agreement has its own detail
      // screen. An "interested" row is still a draft agreement, which the
      // agreement endpoint deliberately never returns, so it must not link
      // there.
      rowActions={(opportunity) =>
        opportunity.agreementStatus === OpportunityAgreementStatus.None ? (
          <Button
            dialog={() => (
              <OpportunityDialog
                summary={opportunity}
                onRegistered={onChanged}
              />
            )}
          >
            View
          </Button>
        ) : opportunity.agreementStatus ===
          OpportunityAgreementStatus.Interested ? (
          <Button href={opportunityHref(opportunity.companyId)}>
            View Package
          </Button>
        ) : (
          <Button
            href={customerSpecificPricingAgreementHref(
              opportunity.agreementId!,
            )}
          >
            View
          </Button>
        )
      }
    />
  );
}
